import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Phone, Plus, Search, Link2, Unlink, Trash2, PhoneOutgoing, ShieldCheck, ShieldOff, Download, RefreshCw } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

const COUNTRIES = [
  { code: "US", name: "United States" }, { code: "GB", name: "United Kingdom" }, { code: "CA", name: "Canada" },
  { code: "AU", name: "Australia" }, { code: "AE", name: "United Arab Emirates" }, { code: "PK", name: "Pakistan" },
  { code: "IN", name: "India" }, { code: "DE", name: "Germany" }, { code: "FR", name: "France" }, { code: "ES", name: "Spain" },
];

export default function PhoneNumbersPage() {
  const [twCfg, setTwCfg] = useState(null);
  const [numbers, setNumbers] = useState([]);
  const [agents, setAgents] = useState([]);
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  // connect dialog
  const [connectOpen, setConnectOpen] = useState(false);
  const [twForm, setTwForm] = useState({ account_sid: "", auth_token: "" });

  // buy dialog
  const [buyOpen, setBuyOpen] = useState(false);
  const [buyForm, setBuyForm] = useState({ country: "US", area_code: "" });
  const [searchResults, setSearchResults] = useState([]);

  // import existing
  const [importOpen, setImportOpen] = useState(false);
  const [importE164, setImportE164] = useState("");

  // test outbound
  const [testNum, setTestNum] = useState("");

  const loadAll = async () => {
    const [c, n, ag] = await Promise.all([
      api.get("/phone-numbers/twilio-config"),
      api.get("/phone-numbers").catch(() => ({ data: [] })),
      api.get("/agents"),
    ]);
    setTwCfg(c.data); setNumbers(n.data || []); setAgents(ag.data);
    if (n.data && n.data[0] && !selected) setSelected(n.data[0]);
  };
  useEffect(() => { loadAll(); /* eslint-disable-next-line */ }, []);

  const connectTwilio = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/phone-numbers/twilio-config", twForm);
      const n = Number(data?.imported || 0);
      toast.success(
        n > 0
          ? `Twilio connected · ${n} existing number${n === 1 ? "" : "s"} imported`
          : "Twilio connected · no existing numbers found — buy or import one below"
      );
      setConnectOpen(false); setTwForm({ account_sid: "", auth_token: "" });
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Connect failed"); }
    finally { setBusy(false); }
  };

  const disconnectTwilio = async () => {
    if (!window.confirm("Disconnect Twilio? Your numbers stay in Twilio.")) return;
    await api.delete("/phone-numbers/twilio-config"); toast.success("Disconnected"); loadAll();
  };

  const searchNumbers = async () => {
    setBusy(true); setSearchResults([]);
    try {
      const { data } = await api.get("/phone-numbers/search", {
        params: { country: buyForm.country, area_code: buyForm.area_code || undefined },
      });
      setSearchResults(data);
      if (data.length === 0) toast.info("No numbers matched. Try a different area code.");
    } catch (err) { toast.error(err?.response?.data?.detail || "Search failed"); }
    finally { setBusy(false); }
  };

  const buy = async (phone_number) => {
    setBusy(true);
    try {
      const { data } = await api.post("/phone-numbers/buy", { phone_number });
      toast.success(data.vapi_phone_number_id
        ? `${phone_number} purchased & registered on the calling server`
        : `${phone_number} purchased — Calling-server registration pending (open the number to retry).`
      );
      setBuyOpen(false); setSearchResults([]);
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Purchase failed"); }
    finally { setBusy(false); }
  };

  const importExisting = async () => {
    const e164 = importE164.trim();
    if (!/^\+\d{7,18}$/.test(e164)) return toast.error("Enter an E.164 number, e.g. +447700900123");
    setBusy(true);
    try {
      await api.post("/phone-numbers/import", { phone_number: e164 });
      toast.success(`${e164} imported and registered on the calling server`);
      setImportOpen(false); setImportE164("");
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Import failed"); }
    finally { setBusy(false); }
  };

  const syncFromTwilio = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/phone-numbers/sync-from-twilio");
      const parts = [];
      if (data.added) parts.push(`${data.added} added`);
      if (data.updated) parts.push(`${data.updated} re-registered`);
      toast.success(parts.length ? `Synced · ${parts.join(" · ")}` : `All ${data.total_in_twilio} numbers already in sync`);
      loadAll();
    } catch (err) { toast.error(err?.response?.data?.detail || "Sync failed"); }
    finally { setBusy(false); }
  };

  const registerVapi = async () => {
    if (!selected) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/phone-numbers/${selected.id}/register-vapi`);
      toast.success(`Registered on the calling server · ${(data.vapi_phone_number_id || "").slice(0, 8)}…`);
      await loadAll();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Calling-server registration failed");
    } finally { setBusy(false); }
  };

  const attach = async (agent_id) => {
    if (!selected) return;
    setBusy(true);
    try {
      await api.post(`/phone-numbers/${selected.id}/attach`, { agent_id });
      toast.success("Number attached & webhook auto-configured");
      const { data } = await api.get("/phone-numbers");
      setNumbers(data); setSelected(data.find((x) => x.id === selected.id));
    } catch (err) { toast.error(err?.response?.data?.detail || "Attach failed"); }
    finally { setBusy(false); }
  };

  const detach = async () => {
    if (!selected) return;
    await api.post(`/phone-numbers/${selected.id}/detach`);
    toast.success("Detached");
    const { data } = await api.get("/phone-numbers");
    setNumbers(data); setSelected(data.find((x) => x.id === selected.id));
  };

  const release = async () => {
    if (!selected || !window.confirm(`Release ${selected.phone_number}? This permanently removes it from your Twilio account.`)) return;
    await api.delete(`/phone-numbers/${selected.id}`);
    toast.success("Released");
    setSelected(null); loadAll();
  };

  const testOutbound = async () => {
    if (!testNum || !selected) return;
    setBusy(true);
    try {
      await api.post(`/phone-numbers/${selected.id}/test-outbound`, { to_number: testNum });
      toast.success("Test call queued");
      setTestNum("");
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  // ---- Render ----
  if (twCfg === null) return <div className="text-sm text-[#4B5563]">Loading…</div>;

  if (!twCfg.connected) {
    return (
      <div data-testid="phone-numbers-page" className="max-w-2xl">
        <h1 className="font-display font-extrabold text-3xl">Phone Numbers</h1>
        <p className="text-sm text-[#4B5563] mb-8">Connect your Twilio account once, then buy and attach numbers to agents in seconds.</p>
        <div className="hs-card p-8" data-testid="twilio-not-connected-card">
          <div className="grid md:grid-cols-2 gap-8">
            {/* LEFT — value prop */}
            <div>
              <div className="inline-flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-[#F97316]">
                <Phone className="w-3.5 h-3.5" /> Step 1 of outbound calling
              </div>
              <div className="font-display font-extrabold text-2xl mt-3">Connect Twilio to get a phone number</div>
              <p className="text-sm text-[#4B5563] mt-2">
                HelloSir doesn&apos;t resell numbers — you buy them at Twilio&apos;s cost (usually $1/month in the US). We connect your Twilio account to the calling server automatically so campaigns just work.
              </p>

              <ol className="mt-6 space-y-3 text-sm">
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFF7ED] text-[#EA580C] font-mono text-xs font-bold flex items-center justify-center">1</span>
                  <div>
                    <div className="font-semibold">Create a free Twilio account (2 min)</div>
                    <div className="text-xs text-[#4B5563]">
                      <a href="https://www.twilio.com/try-twilio" target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline">
                        twilio.com/try-twilio ↗
                      </a> — no credit card required for the $15 trial credit.
                    </div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFF7ED] text-[#EA580C] font-mono text-xs font-bold flex items-center justify-center">2</span>
                  <div>
                    <div className="font-semibold">Copy your Account SID + Auth Token</div>
                    <div className="text-xs text-[#4B5563]">
                      Available on the top-right of the <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline">Twilio Console home ↗</a>. Both are strings — we encrypt them at rest.
                    </div>
                  </div>
                </li>
                <li className="flex gap-3">
                  <span className="flex-shrink-0 w-6 h-6 rounded-full bg-[#FFF7ED] text-[#EA580C] font-mono text-xs font-bold flex items-center justify-center">3</span>
                  <div>
                    <div className="font-semibold">Hit &quot;Connect&quot; below</div>
                    <div className="text-xs text-[#4B5563]">Instantly unlocks &quot;Buy a number&quot;, &quot;Import existing&quot;, and &quot;Sync all from Twilio&quot; on this page.</div>
                  </div>
                </li>
              </ol>

              <Dialog open={connectOpen} onOpenChange={setConnectOpen}>
                <DialogTrigger asChild>
                  <button data-testid="connect-twilio-button" className="btn-primary mt-8 inline-flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4" /> Connect Twilio
                  </button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader><DialogTitle>Connect Twilio</DialogTitle></DialogHeader>
                  <div className="space-y-3">
                    <div>
                      <label className="text-xs font-medium block mb-1">Account SID</label>
                      <input data-testid="twilio-sid-input" value={twForm.account_sid} onChange={(e) => setTwForm({ ...twForm, account_sid: e.target.value })} placeholder="AC…" className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm font-mono" />
                    </div>
                    <div>
                      <label className="text-xs font-medium block mb-1">Auth Token</label>
                      <input data-testid="twilio-token-input" type="password" value={twForm.auth_token} onChange={(e) => setTwForm({ ...twForm, auth_token: e.target.value })} className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm font-mono" />
                    </div>
                    <div className="text-xs text-[#4B5563]">Get them from <a href="https://console.twilio.com" target="_blank" rel="noopener noreferrer" className="text-[#F97316] hover:underline">console.twilio.com ↗</a> → Account Info.</div>
                    <button data-testid="twilio-connect-save" onClick={connectTwilio} disabled={busy || !twForm.account_sid || !twForm.auth_token} className="btn-primary w-full">{busy ? "Verifying…" : "Connect"}</button>
                  </div>
                </DialogContent>
              </Dialog>
            </div>

            {/* RIGHT — preview of what unlocks after connect */}
            <div className="bg-gradient-to-br from-[#FFF7ED] to-white border border-[#F97316]/25 rounded-[10px] p-6">
              <div className="text-[10px] font-mono uppercase tracking-widest text-[#4B5563] mb-3">After you connect, you can:</div>
              <div className="space-y-3">
                <PreviewRow Icon={Plus} title="Buy a new number" body="Search Twilio's catalog in 100+ countries and buy in-portal. $1/mo typical." />
                <PreviewRow Icon={Download} title="Import an existing number" body="Already own a Twilio number? Paste the E.164 and we register it on the calling server for you." />
                <PreviewRow Icon={RefreshCw} title="Sync all from Twilio" body="One-click bulk import of every number in your Twilio account." />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="phone-numbers-page" className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-extrabold text-3xl">Phone Numbers</h1>
          <p className="text-sm text-[#4B5563] flex items-center gap-2 mt-1">
            <ShieldCheck className="w-3.5 h-3.5 text-[#10B981]" /> Twilio connected · <span className="font-mono">{twCfg.account_sid_masked}</span>
            <button onClick={disconnectTwilio} className="text-xs text-[#EF4444] hover:underline ml-2" data-testid="twilio-disconnect">Disconnect</button>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={syncFromTwilio}
            disabled={busy}
            className="btn-outline text-sm flex items-center gap-2 disabled:opacity-60"
            data-testid="sync-from-twilio-button"
            title="Pull every number in your Twilio account into HelloSir and register them on the calling server"
          >
            <RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> Sync all from Twilio
          </button>

          <Dialog open={importOpen} onOpenChange={setImportOpen}>
            <DialogTrigger asChild>
              <button className="btn-outline text-sm flex items-center gap-2" data-testid="import-number-button">
                <Download className="w-4 h-4" /> Import existing
              </button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader><DialogTitle>Import an existing Twilio number</DialogTitle></DialogHeader>
              <p className="text-sm text-[#4B5563]">
                Enter a number you already own in your connected Twilio account. We&apos;ll register it on the calling server so it&apos;s immediately available for outbound campaigns.
              </p>
              <div className="mt-4">
                <label className="text-xs font-medium block mb-1">Phone number (E.164)</label>
                <input
                  data-testid="import-e164-input"
                  value={importE164}
                  onChange={(e) => setImportE164(e.target.value)}
                  placeholder="+447700900123"
                  className="w-full border border-[#E2E8F0] px-3 py-2 rounded-sm font-mono text-sm"
                />
              </div>
              <button
                onClick={importExisting}
                disabled={busy}
                className="btn-primary w-full mt-4 disabled:opacity-60"
                data-testid="import-submit-button"
              >
                {busy ? "Importing…" : "Import & register"}
              </button>
            </DialogContent>
          </Dialog>

          <Dialog open={buyOpen} onOpenChange={(v) => { setBuyOpen(v); if (!v) setSearchResults([]); }}>
          <DialogTrigger asChild>
            <button className="btn-primary flex items-center gap-2" data-testid="buy-number-button"><Plus className="w-4 h-4" /> Buy a number</button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader><DialogTitle>Search & buy a Twilio number</DialogTitle></DialogHeader>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs font-medium block mb-1">Country</label>
                <select data-testid="buy-country-select" value={buyForm.country} onChange={(e) => setBuyForm({ ...buyForm, country: e.target.value })} className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm bg-white">
                  {COUNTRIES.map((c) => <option key={c.code} value={c.code}>{c.name} ({c.code})</option>)}
                </select>
              </div>
              <div className="col-span-2 flex gap-2 items-end">
                <div className="flex-1">
                  <label className="text-xs font-medium block mb-1">Area code / contains</label>
                  <input data-testid="buy-area-input" value={buyForm.area_code} onChange={(e) => setBuyForm({ ...buyForm, area_code: e.target.value })} placeholder="415 or 'love'" className="w-full border border-[#E2E8F0] px-2 py-2 rounded-sm text-sm" />
                </div>
                <button onClick={searchNumbers} disabled={busy} className="btn-primary text-sm flex items-center gap-1" data-testid="buy-search-button"><Search className="w-3 h-3" /> {busy ? "Searching…" : "Search"}</button>
              </div>
            </div>
            <div className="mt-4 max-h-80 overflow-auto border border-[#E2E8F0]">
              {searchResults.length === 0 ? (
                <div className="p-6 text-center text-xs text-[#4B5563]">Search above to see available numbers.</div>
              ) : searchResults.map((n) => (
                <div key={n.phone_number} className="flex items-center justify-between p-3 border-b border-[#E2E8F0] text-sm" data-testid={`search-result-${n.phone_number.replace(/[^a-zA-Z0-9]/g,'')}`}>
                  <div>
                    <div className="font-mono font-medium">{n.friendly_name}</div>
                    <div className="text-xs text-[#4B5563]">{[n.locality, n.region, n.iso_country].filter(Boolean).join(" · ")} · {n.capabilities.voice && "Voice"}{n.capabilities.sms && " · SMS"}</div>
                  </div>
                  <button onClick={() => buy(n.phone_number)} disabled={busy} className="btn-outline text-xs">Buy</button>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      {numbers.length === 0 ? (
        <div className="hs-card p-12 text-center">
          <Phone className="w-10 h-10 mx-auto text-[#4B5563]" />
          <div className="font-display font-bold mt-4">No numbers yet</div>
          <div className="text-sm text-[#4B5563]">Buy a new Twilio number above, or click &quot;Sync all from Twilio&quot; to pull numbers you already own.</div>
        </div>
      ) : (
        <div className="grid lg:grid-cols-12 gap-6">
          <div className="lg:col-span-4 space-y-2">
            {numbers.map((n) => (
              <button
                key={n.id}
                onClick={() => setSelected(n)}
                data-testid={`number-row-${n.id}`}
                className={`w-full hs-card p-4 text-left ${selected?.id === n.id ? "ring-2 ring-[#0044FF] border-[#0044FF]" : ""}`}
              >
                <div className="font-mono font-bold">{n.phone_number}</div>
                <div className="text-xs text-[#4B5563] mt-1 flex items-center gap-2">
                  {n.iso_country || "—"} ·
                  {n.agent_id ? (
                    <span className="inline-flex items-center gap-1 text-[#10B981]"><Link2 className="w-3 h-3" /> {n.agent_name || "attached"}</span>
                  ) : (
                    <span className="text-[#4B5563]">unattached</span>
                  )}
                </div>
              </button>
            ))}
          </div>

          <div className="lg:col-span-8">
            {!selected ? (
              <div className="hs-card p-8 text-center text-sm text-[#4B5563]">Select a number from the list.</div>
            ) : (
              <div className="space-y-4">
                <div className="hs-card p-6">
                  <div className="flex items-start justify-between">
                    <div>
                      <div className="font-display font-extrabold text-2xl font-mono">{selected.phone_number}</div>
                      <div className="text-xs text-[#4B5563] mt-1 font-mono">{selected.twilio_sid}</div>
                    </div>
                    <button onClick={release} className="text-[#EF4444] btn-outline text-xs flex items-center gap-1" data-testid="release-number-button"><Trash2 className="w-3 h-3" /> Release</button>
                  </div>
                </div>

                {/* Calling-server registration status card — surfaced explicitly because
                    without a vapi_phone_number_id, outbound campaigns can't
                    use this number even though Twilio inbound works fine. */}
                <div className="hs-card p-6" data-testid="vapi-status-card">
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <div className="font-display font-bold text-sm mb-1">Calling-server registration</div>
                      {selected.vapi_phone_number_id ? (
                        <div className="text-xs text-[#10B981] font-mono inline-flex items-center gap-1.5">
                          <ShieldCheck className="w-3.5 h-3.5" /> registered · {selected.vapi_phone_number_id.slice(0, 8)}…
                        </div>
                      ) : (
                        <>
                          <div className="text-xs text-[#F59E0B] font-mono inline-flex items-center gap-1.5"><ShieldOff className="w-3.5 h-3.5" /> not registered — outbound campaigns won&apos;t see this number yet</div>
                          <div className="text-[11px] text-[#4B5563] mt-1.5">
                            Registers this number on the calling server so &quot;New campaign&quot; can dial from it.
                          </div>
                        </>
                      )}
                    </div>
                    {!selected.vapi_phone_number_id && (
                      <button
                        onClick={registerVapi}
                        disabled={busy}
                        className="btn-primary text-xs whitespace-nowrap disabled:opacity-60"
                        data-testid="register-vapi-button"
                      >
                        {busy ? "Registering…" : "Register on Calling Server"}
                      </button>
                    )}
                  </div>
                </div>

                <div className="hs-card p-6">
                  <div className="font-display font-bold mb-3">Attach to agent</div>
                  {selected.agent_id ? (
                    <div className="flex items-center justify-between">
                      <div className="text-sm">
                        Attached to <strong>{selected.agent_name}</strong>. Inbound webhook auto-configured.
                      </div>
                      <button onClick={detach} className="btn-outline text-xs flex items-center gap-1" data-testid="detach-number-button"><Unlink className="w-3 h-3" /> Detach</button>
                    </div>
                  ) : agents.length === 0 ? (
                    <div className="text-sm text-[#4B5563]">Create an agent first, then come back.</div>
                  ) : (
                    <div className="space-y-2">
                      {agents.filter((a) => a.type === "voice_calling" || a.type === "web_voice" || a.type === "web_chat").map((a) => (
                        <button
                          key={a.id}
                          onClick={() => attach(a.id)}
                          disabled={busy}
                          data-testid={`attach-agent-${a.id}`}
                          className="w-full flex items-center justify-between p-3 border border-[#E2E8F0] text-sm hover:bg-[#F9FAFB] disabled:opacity-50"
                        >
                          <div>
                            <div className="font-medium">{a.name}</div>
                            <div className="text-xs text-[#4B5563]">{a.type} · {a.voice_id}</div>
                          </div>
                          <Link2 className="w-4 h-4 text-[#0044FF]" />
                        </button>
                      ))}
                    </div>
                  )}
                </div>

                {selected.voice_url && (
                  <div className="hs-card p-6">
                    <div className="font-display font-bold mb-2 flex items-center gap-2"><ShieldCheck className="w-4 h-4 text-[#10B981]" /> Webhook configured</div>
                    <div className="font-mono text-xs break-all bg-[#F3F4F6] p-3">{selected.voice_url}</div>
                  </div>
                )}

                {selected.agent_id && (
                  <div className="hs-card p-6">
                    <div className="font-display font-bold mb-3 flex items-center gap-2"><PhoneOutgoing className="w-4 h-4" /> Test outbound</div>
                    <div className="flex gap-2">
                      <input data-testid="test-outbound-number" value={testNum} onChange={(e) => setTestNum(e.target.value)} placeholder="+15558675309" className="flex-1 border border-[#E2E8F0] px-3 py-2 rounded-sm text-sm font-mono" />
                      <button onClick={testOutbound} disabled={busy || !testNum} className="btn-primary text-sm" data-testid="test-outbound-button">{busy ? "Calling…" : "Call now"}</button>
                    </div>
                    <div className="text-xs text-[#4B5563] mt-2">A live call will be placed from <strong>{selected.phone_number}</strong> using your attached agent.</div>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PreviewRow({ Icon, title, body }) {
  return (
    <div className="flex gap-3">
      <span className="flex-shrink-0 w-8 h-8 rounded-[6px] bg-white border border-[#F97316]/30 text-[#F97316] flex items-center justify-center">
        <Icon className="w-4 h-4" />
      </span>
      <div>
        <div className="text-sm font-semibold text-[#0F1115]">{title}</div>
        <div className="text-xs text-[#4B5563] leading-snug">{body}</div>
      </div>
    </div>
  );
}

